/**
 * Sign in again without leaving the screen (desktop only).
 *
 * When the server rejects the session (an expired token), the web app wipes the session and
 * navigates to /login. On a POS terminal that discards whatever is on screen, such as a
 * half-entered bill. Here the same user re-enters their password in place: the app's own
 * LOGIN_START saga refreshes the token and re-seeds the credential vault (which also lets
 * the sync engine resume), and the screen underneath is untouched.
 *
 * The email is locked to the identity in the credential vault. Signing in as someone else
 * in place would mix one business's session with another's data still on screen; that has
 * to go through a full logout.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography,
} from '@mui/material';
import authActions from '../layouts/Auth/redux/actions';
import { SESSION_EXPIRED_EVENT, isExpired, logoutUser } from '../helpers/utilities';

function errorText(loginError) {
  const raw = loginError && loginError.error && loginError.error.message;
  if (typeof raw !== 'string') return 'Could not sign in. Check your connection and try again.';
  const parts = raw.split('|');
  return parts.length > 1 ? parts[1] : raw;
}

export default function ReauthDialog() {
  const dispatch = useDispatch();
  const { loggedInUser, loginError, loginLoader } = useSelector((state) => state.authReducer);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const submitted = useRef(false);

  useEffect(() => {
    const onExpired = async () => {
      if (submitted.current) return; // a sign-in is already in progress
      setPassword('');
      setError('');
      let session = null;
      try {
        session = window.electron && window.electron.auth ? await window.electron.auth.getSession() : null;
      } catch (e) {
        session = null;
      }
      setEmail((session && session.email) || '');
      setOpen(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    if (!submitted.current || loginLoader) return;
    if (loggedInUser) {
      submitted.current = false;
      setOpen(false);
      setPassword('');
    } else if (loginError) {
      submitted.current = false;
      setError(errorText(loginError));
    }
  }, [loggedInUser, loginError, loginLoader]);

  if (!open) return null;

  const signIn = (e) => {
    e.preventDefault();
    if (!password) {
      setError('Enter your password');
      return;
    }
    setError('');
    submitted.current = true;
    dispatch({ type: authActions.LOGIN_START, loginData: { email, password } });
  };

  // No identity to lock to: fall back to the full sign-in screen rather than allow an
  // in-place sign-in as an arbitrary user.
  if (!email) {
    return (
      <Dialog open maxWidth="xs" fullWidth>
        <DialogTitle>Your sign-in has expired</DialogTitle>
        <DialogContent dividers>
          <Typography>Please sign in again to continue.</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => isExpired()}>Sign in</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <form onSubmit={signIn}>
        <DialogTitle>Your sign-in has expired</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your password to continue. Nothing you have entered on this screen will be lost.
          </Typography>
          <TextField label="Email or mobile" value={email} fullWidth margin="dense" InputProps={{ readOnly: true }} />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="dense"
            autoFocus
            error={Boolean(error)}
            helperText={error || ' '}
            inputProps={{ 'aria-label': 'Password' }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={Boolean(loginLoader)} onClick={() => logoutUser()}>
            Not you? Log out
          </Button>
          <Button type="submit" variant="contained" disabled={Boolean(loginLoader)}>
            {loginLoader ? 'Signing in…' : 'Sign in'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
